import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from '@/components/ui/dialog';
import {
  Trophy, Medal, Target, Sparkles, BarChart3, Search, ClipboardCheck,
  Brain, Heart, Users, Award, ChevronRight, CheckCircle2, Loader2,
  Activity, Accessibility, Eye, HandHeart, GraduationCap, Home, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------
// Shared registration dialog used by both academy pages
// ---------------------------------------------------------------
function RegistrationDialog({ academy, children }) {
  const { lang } = useApp();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    full_name: '', age: '', disability: '', sport_interest: '',
    phone: '', email: '', notes: '',
  });

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.error(lang === 'ar' ? 'الاسم والهاتف مطلوبان' : 'Name and phone are required');
      return;
    }
    setBusy(true);
    try {
      await api.post('/academy/register', { ...form, academy });
      setDone(true);
      toast.success(lang === 'ar' ? '✓ تم استلام طلب التسجيل' : '✓ Registration received');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setBusy(false);
  };

  const reset = () => { setForm({ full_name: '', age: '', disability: '', sport_interest: '', phone: '', email: '', notes: '' }); setDone(false); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="academy-register-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5 text-primary" />
            {lang === 'ar' ? 'استمارة التسجيل' : 'Registration form'}
          </DialogTitle>
          <DialogDescription>{academy}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="text-center py-8" data-testid="register-done">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
            <h3 className="font-black text-lg mb-1">{lang === 'ar' ? 'تم تسجيلك بنجاح' : 'Successfully registered'}</h3>
            <p className="text-sm text-muted-foreground">{lang === 'ar' ? 'سيتواصل فريق الأكاديمية معك قريباً.' : 'The academy team will contact you shortly.'}</p>
          </div>
        ) : (
          <form className="space-y-3" onSubmit={submit}>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">{lang === 'ar' ? 'الاسم الكامل *' : 'Full name *'}</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="ar-name" /></div>
              <div><Label className="text-xs">{lang === 'ar' ? 'العمر' : 'Age'}</Label><Input type="number" min={3} max={70} value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="ar-age" /></div>
            </div>
            <div><Label className="text-xs">{lang === 'ar' ? 'الإعاقة / الحالة' : 'Disability / condition'}</Label><Input value={form.disability} onChange={(e) => setForm({ ...form, disability: e.target.value })} data-testid="ar-disability" /></div>
            <div><Label className="text-xs">{lang === 'ar' ? 'الرياضة المفضّلة' : 'Preferred sport'}</Label><Input value={form.sport_interest} onChange={(e) => setForm({ ...form, sport_interest: e.target.value })} data-testid="ar-sport" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">{lang === 'ar' ? 'الهاتف *' : 'Phone *'}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ar-phone" /></div>
              <div><Label className="text-xs">{lang === 'ar' ? 'البريد' : 'Email'}</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ar-email" /></div>
            </div>
            <div><Label className="text-xs">{lang === 'ar' ? 'ملاحظات' : 'Notes'}</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="ar-notes" /></div>
            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90 h-10 font-bold" data-testid="ar-submit">
              {busy ? <><Loader2 className="w-4 h-4 me-2 animate-spin" />{lang === 'ar' ? 'يُرسل...' : 'Sending...'}</> : (lang === 'ar' ? 'إرسال الطلب' : 'Submit')}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------
// Reusable academy layout (shared by both pages)
// ---------------------------------------------------------------
function AcademyLayout({ academyId, title_ar, title_en, subtitle_ar, subtitle_en, hue, about_ar, about_en, target_ar, target_en, services, sports, ai_features, testid }) {
  const { lang } = useApp();
  const isAr = lang === 'ar';
  return (
    <main id="main" className="bg-gradient-to-b from-white via-stone-50 to-white" data-testid={testid}>
      {/* Hero */}
      <section className="relative overflow-hidden" aria-labelledby="academy-title">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-32 -end-32 w-96 h-96 rounded-full blur-3xl" style={{ background: `hsla(${hue}, 80%, 60%, 0.2)` }} />
          <div className="absolute -bottom-32 -start-32 w-96 h-96 rounded-full blur-3xl" style={{ background: `hsla(${hue + 60}, 80%, 60%, 0.2)` }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <Badge className="mb-4 text-xs px-3 py-1 font-bold" style={{ background: `hsl(${hue}, 70%, 35%)`, color: 'white' }}>
            <Sparkles className="w-3 h-3 me-1" />{isAr ? 'منصة همم · أكاديمية ذكية' : 'Himam Platform · Smart Academy'}
          </Badge>
          <h1 id="academy-title" className="text-3xl sm:text-5xl lg:text-6xl font-black leading-tight mb-4 max-w-4xl mx-auto" style={{ color: `hsl(${hue}, 70%, 25%)` }}>
            {isAr ? title_ar : title_en}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
            {isAr ? subtitle_ar : subtitle_en}
          </p>
          <RegistrationDialog academy={academyId}>
            <Button size="lg" className="font-black text-base h-12 px-8 shadow-lg" style={{ background: `hsl(${hue}, 70%, 35%)`, color: 'white' }} data-testid="hero-register-btn">
              <GraduationCap className="w-5 h-5 me-2" />{isAr ? 'سجّل الآن' : 'Register now'}
            </Button>
          </RegistrationDialog>
        </div>
      </section>

      {/* Smart Tools quick links */}
      <section className="px-4 -mt-6 relative z-10" aria-labelledby="smart-tools">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-3" data-testid="smart-tools-grid">
          <Link to={`/academy/talent-assessment?academy=${academyId}`} data-testid="smart-tool-talent" className="block bg-white border-2 border-border rounded-xl p-4 card-elevate hover:border-amber-300 transition-all">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 grid place-items-center text-amber-700 shrink-0"><Brain className="w-5 h-5" /></div>
              <div>
                <div className="font-black text-sm">{isAr ? 'تقييم المواهب' : 'Talent Assessment'}</div>
                <div className="text-[11px] text-muted-foreground">{isAr ? 'اكتشف رياضتك المثاليّة' : 'Discover your sport'}</div>
              </div>
            </div>
          </Link>
          <Link to={`/academy/training?academy=${academyId}`} data-testid="smart-tool-training" className="block bg-white border-2 border-border rounded-xl p-4 card-elevate hover:border-emerald-300 transition-all">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 grid place-items-center text-emerald-700 shrink-0"><Target className="w-5 h-5" /></div>
              <div>
                <div className="font-black text-sm">{isAr ? 'مناهج التدريب الذكية' : 'Smart Training'}</div>
                <div className="text-[11px] text-muted-foreground">{isAr ? 'خطط شخصية بـ AI' : 'Personal AI plans'}</div>
              </div>
            </div>
          </Link>
          {academyId === 'paralympic' ? (
            <Link to="/academy/classification" data-testid="smart-tool-classify" className="block bg-white border-2 border-border rounded-xl p-4 card-elevate hover:border-sky-300 transition-all">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-sky-100 grid place-items-center text-sky-700 shrink-0"><Trophy className="w-5 h-5" /></div>
                <div>
                  <div className="font-black text-sm">{isAr ? 'التصنيف الفني' : 'Functional Classification'}</div>
                  <div className="text-[11px] text-muted-foreground">{isAr ? 'تحليل حركي + IPC' : 'Motion analysis + IPC'}</div>
                </div>
              </div>
            </Link>
          ) : (
            <Link to={`/academy/${academyId}/hub`} data-testid="smart-tool-hub" className="block bg-white border-2 border-border rounded-xl p-4 card-elevate hover:border-violet-300 transition-all">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-100 grid place-items-center text-violet-700 shrink-0"><Sparkles className="w-5 h-5" /></div>
                <div>
                  <div className="font-black text-sm">{isAr ? 'المركز الذكي' : 'Smart Center'}</div>
                  <div className="text-[11px] text-muted-foreground">{isAr ? 'كل الأدوات الذكيّة' : 'All smart tools'}</div>
                </div>
              </div>
            </Link>
          )}
        </div>
      </section>

      {/* About */}
      <Section title={isAr ? 'عن الأكاديمية' : 'About the Academy'} hue={hue} icon={Heart}>
        <p className="text-base sm:text-lg leading-loose text-stone-700 max-w-3xl">{isAr ? about_ar : about_en}</p>
      </Section>

      {/* Target groups */}
      <Section title={isAr ? 'الفئات المستهدفة' : 'Target Groups'} hue={hue} icon={Users} bg="bg-stone-50">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="target-groups">
          {(isAr ? target_ar : target_en).map((g, i) => (
            <div key={i} className="bg-white border border-border rounded-xl p-4 card-elevate flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0" style={{ background: `hsla(${hue}, 70%, 35%, 0.12)`, color: `hsl(${hue}, 70%, 35%)` }}>
                <Accessibility className="w-5 h-5" />
              </div>
              <span className="font-semibold text-sm">{g}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Services */}
      <Section title={isAr ? 'الخدمات' : 'Services'} hue={hue} icon={ClipboardCheck}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="services-grid">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <Card key={i} className="card-elevate h-full">
                <CardContent className="p-4">
                  <div className="w-10 h-10 rounded-lg grid place-items-center mb-2" style={{ background: `hsla(${hue}, 70%, 35%, 0.12)`, color: `hsl(${hue}, 70%, 35%)` }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-bold text-sm leading-tight">{isAr ? s.ar : s.en}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </Section>

      {/* Sports */}
      <Section title={isAr ? 'الرياضات' : 'Sports'} hue={hue} icon={Trophy} bg="bg-stone-50">
        <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="sports-grid">
          {sports.map((sp, i) => (
            <div key={i} className="bg-white border-2 border-border rounded-xl p-4 text-center card-elevate hover:border-current transition-all" style={{ borderColor: i % 3 === 0 ? `hsla(${hue}, 70%, 70%, 0.35)` : undefined }}>
              <Medal className="w-7 h-7 mx-auto mb-2" style={{ color: `hsl(${hue + (i * 8)}, 70%, 40%)` }} />
              <div className="font-bold text-sm">{isAr ? sp.ar : sp.en}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* AI features */}
      <Section title={isAr ? 'مزايا الذكاء الاصطناعي' : 'AI Features'} hue={hue} icon={Sparkles}>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="ai-features">
          {ai_features.map((f, i) => {
            const Icon = f.icon;
            return (
              <Card key={i} className="card-elevate overflow-hidden">
                <CardContent className="p-5 flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br grid place-items-center text-white shrink-0 shadow-md" style={{ background: `linear-gradient(135deg, hsl(${hue}, 70%, 45%), hsl(${hue + 40}, 70%, 50%))` }}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-black text-sm mb-1">{isAr ? f.title_ar : f.title_en}</div>
                    <div className="text-xs text-muted-foreground leading-relaxed">{isAr ? f.desc_ar : f.desc_en}</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </Section>

      {/* CTA footer */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center rounded-2xl p-8 sm:p-12 text-white" style={{ background: `linear-gradient(135deg, hsl(${hue}, 70%, 30%), hsl(${hue + 60}, 70%, 35%))` }}>
          <Trophy className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <h2 className="text-2xl sm:text-3xl font-black mb-3">{isAr ? 'ابدأ رحلتك الرياضية اليوم' : 'Start your athletic journey today'}</h2>
          <p className="text-sm sm:text-base opacity-90 mb-6 max-w-xl mx-auto">{isAr ? 'انضم لمئات الرياضيين الذين يطوّرون مهاراتهم على منصّتنا.' : 'Join hundreds of athletes growing on our platform.'}</p>
          <RegistrationDialog academy={academyId}>
            <Button size="lg" variant="secondary" className="font-black h-12 px-8" data-testid="cta-register-btn">
              <GraduationCap className="w-5 h-5 me-2" />{isAr ? 'سجّل الآن' : 'Register'}
            </Button>
          </RegistrationDialog>
        </div>
      </section>
    </main>
  );
}

function Section({ title, icon: Icon, hue, children, bg = '' }) {
  return (
    <section className={`py-12 sm:py-16 px-4 ${bg}`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-1 h-8 rounded-full" style={{ background: `hsl(${hue}, 70%, 35%)` }} aria-hidden="true" />
          <Icon className="w-6 h-6" style={{ color: `hsl(${hue}, 70%, 35%)` }} aria-hidden="true" />
          <h2 className="text-2xl sm:text-3xl font-black" style={{ color: `hsl(${hue}, 70%, 25%)` }}>{title}</h2>
        </div>
        {children}
      </div>
    </section>
  );
}

// ============================================================
// PAGE 1: Paralympic Sports Academy
// ============================================================
export function ParalympicAcademyPage() {
  return (
    <AcademyLayout
      testid="academy-paralympic"
      academyId="paralympic"
      hue={210}
      title_ar="الأكاديمية الذكية للتدريب والتصنيف في الرياضات البارالمبية"
      title_en="AI-Powered Paralympic Sports Training & Classification Academy"
      subtitle_ar="منصة متكاملة بالذكاء الاصطناعي لتقييم وتصنيف وتدريب الرياضيين البارالمبيين، تبدأ من اكتشاف الموهبة وتمتد حتى المنافسات الدولية."
      subtitle_en="A complete AI-powered platform for assessing, classifying and training Paralympic athletes — from talent discovery to international competition."
      about_ar="تجمع الأكاديمية بين التصنيف الوظيفي المُعتمد، وتقييم القدرات الفردية، وتوصيات الذكاء الاصطناعي لاختيار الرياضة الأنسب، مع خطط تدريب مخصّصة وتتبّع تقدّم لحظي وشهادات رقمية موثّقة."
      about_en="The academy combines functional classification, individual assessment, AI sport recommendations, personalised training plans, real-time progress tracking, and verifiable digital certificates."
      target_ar={['ذوي الإعاقة الحركية', 'ذوي الإعاقة البصرية', 'الشلل الدماغي', 'البتر الجزئي أو الكلي', 'فئات بارالمبية مؤهّلة']}
      target_en={['Physical disabilities', 'Visual impairment', 'Cerebral palsy', 'Partial/total amputation', 'Eligible Para classes']}
      services={[
        { ar: 'التصنيف الوظيفي الرياضي', en: 'Functional sports classification', icon: ClipboardCheck },
        { ar: 'تقييم الرياضي', en: 'Athlete assessment', icon: BarChart3 },
        { ar: 'الإرشاد لأهلية الرياضة', en: 'Sport eligibility guidance', icon: CheckCircle2 },
        { ar: 'توصية رياضة بالذكاء الاصطناعي', en: 'AI sport recommendation', icon: Sparkles },
        { ar: 'خطط تدريب فردية', en: 'Individual training plans', icon: Target },
        { ar: 'تتبّع الأداء', en: 'Performance tracking', icon: Activity },
        { ar: 'اكتشاف المواهب', en: 'Talent identification', icon: Search },
        { ar: 'شهادات رقمية بـ QR', en: 'Digital certificates with QR', icon: Award },
      ]}
      sports={[
        { ar: 'ألعاب القوى البارالمبية', en: 'Para Athletics' },
        { ar: 'السباحة البارالمبية', en: 'Para Swimming' },
        { ar: 'البوتشيا', en: 'Boccia' },
        { ar: 'كرة سلة الكراسي المتحركة', en: 'Wheelchair Basketball' },
        { ar: 'رفع الأثقال البارالمبي', en: 'Para Powerlifting' },
        { ar: 'تنس الطاولة البارالمبي', en: 'Para Table Tennis' },
        { ar: 'الريشة الطائرة البارالمبية', en: 'Para Badminton' },
        { ar: 'الرماية', en: 'Shooting' },
      ]}
      ai_features={[
        { title_ar: 'تحليل نتائج التقييم', title_en: 'Analyse assessment results', desc_ar: 'يقرأ النظام نتائج التقييم الوظيفي والبدني ويُعطي صورة دقيقة عن قدرات الرياضي.', desc_en: 'AI reads functional & physical results and gives a precise athlete capability profile.', icon: BarChart3 },
        { title_ar: 'إنشاء خطط تدريب', title_en: 'Generate training plans', desc_ar: 'خطط أسبوعية وشهرية مخصصة وفق المستوى والهدف.', desc_en: 'Weekly and monthly plans tailored to level and goal.', icon: Target },
        { title_ar: 'تقارير تقدّم تلقائية', title_en: 'Auto progress reports', desc_ar: 'تقارير دورية للمدرب والأسرة ولجنة التصنيف.', desc_en: 'Periodic reports for coach, family, and classification panel.', icon: FileText },
        { title_ar: 'توصية الرياضة المناسبة', title_en: 'Recommend the right sport', desc_ar: 'محرّك توصيات يربط القدرات بأكثر ٨ رياضات بارالمبية ملاءمة.', desc_en: 'Recommendation engine matching abilities to the 8 most suitable Para sports.', icon: Sparkles },
      ]}
    />
  );
}

// ============================================================
// PAGE 2: Special Olympics Academy
// ============================================================
export function SpecialOlympicsAcademyPage() {
  return (
    <AcademyLayout
      testid="academy-special-olympics"
      academyId="special-olympics"
      hue={285}
      title_ar="الأكاديمية الذكية للتدريب لرياضات الأولمبياد الخاص"
      title_en="AI-Powered Special Olympics Training Academy"
      subtitle_ar="بيئة محبّبة وممتعة بالذكاء الاصطناعي لتدريب ذوي الإعاقة الذهنية، وقياس جاهزيتهم الحركية، وتمكينهم من المشاركة الرياضية والاجتماعية."
      subtitle_en="A joyful AI-powered environment to train athletes with intellectual disabilities, measure motor readiness, and unlock sport and social participation."
      about_ar="تقدّم الأكاديمية تقييم مهارات حركية، خطط تدريب مولّدة بالذكاء الاصطناعي، برامج Unified Sports تجمع الرياضيين بزملاء غير معاقين، ومتابعة دقيقة للإنجازات والميداليات، مع دعم الأسر والمدرّبين."
      about_en="The academy offers motor skills assessment, AI-generated training plans, Unified Sports programs that pair athletes with non-disabled peers, careful tracking of achievements and medals, plus support for families and coaches."
      target_ar={['ذوي الإعاقة الذهنية', 'متلازمة داون', 'الإعاقات النمائية', 'صعوبات التعلّم الحركية', 'الأطفال والشباب']}
      target_en={['Intellectual disabilities', 'Down syndrome', 'Developmental disabilities', 'Motor learning difficulties', 'Children & youth']}
      services={[
        { ar: 'تقييم المهارات الحركية', en: 'Motor skills assessment', icon: Activity },
        { ar: 'تقييم الجاهزية الرياضية', en: 'Sports readiness evaluation', icon: ClipboardCheck },
        { ar: 'خطط تدريب بالذكاء الاصطناعي', en: 'AI training plans', icon: Sparkles },
        { ar: 'لوحة الأسرة', en: 'Family dashboard', icon: Home },
        { ar: 'برامج Unified Sports', en: 'Unified Sports programs', icon: HandHeart },
        { ar: 'تتبّع التقدّم', en: 'Progress tracking', icon: BarChart3 },
        { ar: 'تدريب المدربين والمتطوّعين', en: 'Coach & volunteer training', icon: GraduationCap },
        { ar: 'تتبّع الإنجازات والميداليات', en: 'Achievement & medal tracking', icon: Medal },
      ]}
      sports={[
        { ar: 'ألعاب القوى', en: 'Athletics' },
        { ar: 'السباحة', en: 'Swimming' },
        { ar: 'كرة القدم', en: 'Football' },
        { ar: 'كرة السلة', en: 'Basketball' },
        { ar: 'البولينج', en: 'Bowling' },
        { ar: 'الدراجات', en: 'Cycling' },
        { ar: 'تنس الطاولة', en: 'Table Tennis' },
        { ar: 'البوتشي', en: 'Bocce' },
      ]}
      ai_features={[
        { title_ar: 'اقتراح الرياضات المناسبة', title_en: 'Suggest suitable sports', desc_ar: 'محرّك يرشد العائلة لأنسب رياضة وفق قدرات الطفل واهتماماته.', desc_en: 'Engine guiding families to the most suitable sport per child capability and interest.', icon: Sparkles },
        { title_ar: 'أنشطة منزلية مخصّصة', title_en: 'Personalised home activities', desc_ar: 'برامج منزلية يومية أو أسبوعية يمكن للأسرة تنفيذها مع الطفل.', desc_en: 'Daily or weekly programs the family can run with the child at home.', icon: Home },
        { title_ar: 'تقارير مبسّطة للأسرة', title_en: 'Family-friendly reports', desc_ar: 'تقارير بصور وأيقونات يسهل فهمها للأسرة.', desc_en: 'Visual reports with icons families easily understand.', icon: FileText },
        { title_ar: 'دعم المدرّب بالتوصيات', title_en: 'Coach training recommendations', desc_ar: 'اقتراحات للتدريب التالي بناءً على تقدّم الرياضي.', desc_en: 'Next-session recommendations based on athlete progress.', icon: GraduationCap },
      ]}
    />
  );
}
